import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  RegulationRequestTypeDto,
  WorkflowType,
  FormType,
} from "../../common/service/server/dto/RegulationRequestTypeDto";
import { usePrimaryOrganizationId } from "./useOrganization";
import { Api } from "../../common/service/Api";

/**
 * Fetch organization-specific regulation request types from the API.
 * Returns request types filtered by org eligibility (profiling opt-out, opt-out, US withdraw consent, etc.)
 * - Same filtering as DSAR + Opt Out form
 */
const fetchOrgRegulationRequestTypes = async (
  organizationId: string,
): Promise<RegulationRequestTypeDto[]> => {
  const typesData = await Api.organization.getDataRequestTypes(organizationId);
  return typesData.map((type) => ({
    ...type,
    requestTypeSlug: type.requestTypeSlug || `${type.regulationLaw.slug}_${type.workflowType}`,
  }));
};

/**
 * Hook to fetch organization-specific regulation request types.
 * Use for Workflow Setup and anywhere we need request types that apply to the org's DSAR + Opt Out forms.
 */
export const useOrgRegulationRequestTypes = () => {
  const organizationId = usePrimaryOrganizationId();

  const {
    data: regulationRequestTypes = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ["org-regulation-request-types", organizationId],
    queryFn: () => fetchOrgRegulationRequestTypes(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const error = queryError ? (queryError as Error).message : null;

  const filterForConsumerForm = useCallback(() => {
    return regulationRequestTypes.filter(
      (type) => type.formType === FormType.YPC || type.formType === FormType.DSAR,
    );
  }, [regulationRequestTypes]);

  const filterByWorkflowType = useCallback(
    (workflowType: WorkflowType) => {
      return regulationRequestTypes.filter((type) => type.workflowType === workflowType);
    },
    [regulationRequestTypes],
  );

  return {
    regulationRequestTypes,
    consumerFormRequestTypes: filterForConsumerForm(),
    loading,
    error,
    filterByWorkflowType,
  };
};
